import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { isValidPeriodCode } from "./contract-period";
import { contractsApi, type ExFactoryCandidate } from "./contracts-api";

type Props =
	| {
			mode: "edit";
			contractId: number;
			producerId: number | null;
			onPendingChange?: (saveFn: (() => Promise<void>) | null) => void;
	  }
	| {
			mode: "create";
			producerId: number | null;
			year: number;
			quarter: string;
			selectedIds: number[];
			onSelectedIdsChange: (ids: number[]) => void;
	  };

function formatDate(iso: string): string {
	return iso.split("-").reverse().join(".");
}

export function ExFactoryPriceListsSection(props: Props) {
	const queryClient = useQueryClient();
	const producerId = props.producerId;

	const candidatesQuery = useQuery<ExFactoryCandidate[]>({
		queryKey:
			props.mode === "edit"
				? ["contracts", props.contractId, "exfactory-candidates"]
				: ["contracts", "exfactory-candidates-by-period", producerId, props.year, props.quarter],
		queryFn: () =>
			props.mode === "edit"
				? contractsApi.getExFactoryCandidates(props.contractId)
				: contractsApi.getExFactoryCandidatesByPeriod(
						producerId as number,
						props.year,
						props.quarter,
					),
		// FARMASTER-FRONTEND-2D: w trybie create `year` bywał NaN (parent nie ustawił
		// jeszcze roku), a `?year=NaN` = backend 400 "year: invalid digit". Nie odpalaj
		// query dopóki rok nie jest skończoną liczbą.
		enabled:
			producerId != null &&
			(props.mode === "edit" || (Number.isFinite(props.year) && isValidPeriodCode(props.quarter))),
	});
	const candidates = candidatesQuery.data ?? [];
	const isLoading = candidatesQuery.isLoading;

	// W trybie edycji: stan lokalny synchronizowany z serwerem, zapis przyciskiem.
	// W trybie tworzenia: wybor trzyma rodzic (forma kontraktu), zapis przy submit.
	const [editSelected, setEditSelected] = useState<Set<number>>(new Set());
	useEffect(() => {
		if (props.mode === "edit") {
			setEditSelected(new Set(candidates.filter((c) => c.isLinked).map((c) => c.id)));
		}
	}, [candidates, props.mode]);

	const selected = props.mode === "edit" ? editSelected : new Set<number>(props.selectedIds);

	const saveMutation = useMutation({
		mutationFn: (ids: number[]) => {
			if (props.mode !== "edit") {
				throw new Error("Save mutation tylko w trybie edycji");
			}
			return contractsApi.setExFactoryPriceLists(props.contractId, ids);
		},
		onSuccess: () => {
			if (props.mode !== "edit") return;
			toast.success("Cenniki zapisane.");
			queryClient.invalidateQueries({ queryKey: ["contracts", String(props.contractId)] });
			queryClient.invalidateQueries({
				queryKey: ["contracts", props.contractId, "exfactory-candidates"],
			});
		},
		onError: (e: Error) => {
			toast.error(`Nie udało się zapisać cenników: ${e.message}`);
		},
	});

	const toggle = (id: number) => {
		if (props.mode === "edit") {
			setEditSelected((prev) => {
				const next = new Set(prev);
				if (next.has(id)) next.delete(id);
				else next.add(id);
				return next;
			});
		} else {
			const next = new Set(props.selectedIds);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			props.onSelectedIdsChange([...next]);
		}
	};

	const linkedIds =
		props.mode === "edit"
			? new Set(candidates.filter((c) => c.isLinked).map((c) => c.id))
			: new Set<number>();
	const isDirty =
		props.mode === "edit" &&
		(editSelected.size !== linkedIds.size || [...editSelected].some((id) => !linkedIds.has(id)));

	// onPendingChange jest stabilna referencją z useRef; saveMutation jest stabilna z useMutation — nie dodajemy do deps
	// eslint-disable-next-line react-hooks/exhaustive-deps
	// biome-ignore lint/correctness/useExhaustiveDependencies: onPendingChange i saveMutation.mutateAsync sa stabilnymi referencjami
	useEffect(() => {
		if (props.mode !== "edit") return;
		props.onPendingChange?.(
			isDirty
				? async () => {
						await saveMutation.mutateAsync([...editSelected]);
					}
				: null,
		);
	}, [isDirty, editSelected, props.mode]);

	if (producerId == null) {
		return (
			<div className="space-y-4 pt-6 border-t">
				<div className="flex items-center gap-2">
					<FileText className="size-4 text-primary" />
					<h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
						Cenniki exfactory
					</h3>
				</div>
				<p className="text-xs text-muted-foreground italic">
					Najpierw wybierz producenta, aby zobaczyć dostępne cenniki.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-4 pt-6 border-t">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<FileText className="size-4 text-primary" />
					<h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
						Cenniki exfactory ({selected.size}
						{candidates.length > 0 ? ` / ${candidates.length}` : ""})
					</h3>
				</div>
				{isDirty && (
					<Button
						type="button"
						variant="default"
						size="sm"
						disabled={saveMutation.isPending}
						onClick={() => saveMutation.mutate([...editSelected])}
						className="h-8 text-xs max-md:h-9"
					>
						{saveMutation.isPending ? (
							<>
								<Loader2 className="size-3 mr-1 animate-spin" /> Zapisywanie…
							</>
						) : (
							"Zapisz wybór"
						)}
					</Button>
				)}
			</div>

			{isLoading ? (
				<p className="text-xs text-muted-foreground italic">Ładowanie cenników…</p>
			) : candidates.length === 0 ? (
				<p className="text-xs text-muted-foreground italic">
					Brak dostępnych cenników dla tego producenta w okresie kontraktu.
				</p>
			) : (
				<>
					<div className="border border-border rounded-lg overflow-hidden bg-card max-md:overflow-x-auto">
						<table className="w-full text-xs border-collapse">
							<thead>
								<tr className="bg-muted/50 border-b border-border">
									<th className="px-4 py-2 w-10 max-md:px-2" />
									<th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase tracking-tighter max-md:px-2">
										Nazwa cennika
									</th>
									<th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase tracking-tighter w-40 max-md:w-auto max-md:px-2">
										Od
									</th>
									<th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase tracking-tighter w-40 max-md:w-auto max-md:px-2">
										Do
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-border/40">
								{candidates.map((c) => (
									<tr
										key={c.id}
										className="hover:bg-muted/20 transition-colors cursor-pointer"
										onClick={() => toggle(c.id)}
									>
										<td className="px-4 py-2 max-md:px-2">
											<input
												type="checkbox"
												checked={selected.has(c.id)}
												onChange={() => toggle(c.id)}
												onClick={(e) => e.stopPropagation()}
												className="size-4 cursor-pointer max-md:size-5"
											/>
										</td>
										<td className="px-4 py-2 font-medium max-md:px-2 max-md:py-3 max-md:break-words">
											{c.name}
										</td>
										<td className="px-4 py-2 font-mono max-md:px-2 max-md:whitespace-nowrap">
											{formatDate(c.startDate)}
										</td>
										<td className="px-4 py-2 font-mono max-md:px-2 max-md:whitespace-nowrap">
											{formatDate(c.endDate)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{selected.size === 0 && (
						<p className="text-xs text-amber-600 italic">
							Brak wybranych cenników — rozliczenie kontraktu nic nie policzy bez cen exfactory.
						</p>
					)}
				</>
			)}
		</div>
	);
}
